import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Card,
  CardContent,
  Grid,
  Menu,
  MenuItem
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';

interface HeaderProps {
  title: string;
  onShowDisclaimer?: () => void;
}

/**
 * Header component with application title and navigation
 */
const Header: React.FC<HeaderProps> = ({ title, onShowDisclaimer }) => {
  const [guideOpen, setGuideOpen] = useState(false);
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<null | HTMLElement>(null);

  const handleOpenGuide = () => {
    setGuideOpen(true);
  };

  const handleCloseGuide = () => {
    setGuideOpen(false);
  };
  
  const handleHelpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHelpMenuAnchor(event.currentTarget);
  };

  const handleHelpClose = () => {
    setHelpMenuAnchor(null);
  };

  const handleShowDisclaimer = () => {
    handleHelpClose();
    if (onShowDisclaimer) {
      onShowDisclaimer();
    }
  };
  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src={process.env.PUBLIC_URL + '/Orb.png'} 
              alt="Tide Analyzer Logo" 
              style={{ height: '32px', marginRight: '10px' }} 
            />
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex' }}>
            {/* Help Button with Help Icon */}
            <Button 
              color="inherit" 
              startIcon={<HelpIcon />}
              onClick={handleHelpClick}
              aria-haspopup="true"
              aria-controls={Boolean(helpMenuAnchor) ? 'help-menu' : undefined}
            >
              Help
            </Button>
            
            {/* Help Menu */}
            <Menu
              id="help-menu"
              anchorEl={helpMenuAnchor}
              open={Boolean(helpMenuAnchor)}
              onClose={handleHelpClose}
            >
              <MenuItem onClick={() => { handleHelpClose(); handleOpenGuide(); }}>Guide</MenuItem>
              <MenuItem onClick={handleShowDisclaimer}>View Disclaimer</MenuItem>
              <MenuItem onClick={handleHelpClose}>About</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Instructions Dialog */}
      <Dialog
        open={guideOpen}
        onClose={handleCloseGuide}
        scroll="paper"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tide Analyzer Guide</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            About This Application
          </Typography>
          <Typography variant="body2" paragraph>
            The Tide Analyzer helps you find optimal times for maritime activities based on tide conditions. 
            It calculates when tides will be suitable for activities that have specific tide height requirements.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Basic Usage
          </Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li>Select a location from the dropdown</li>
              <li>Choose a month to analyze</li>
              <li>Define one or more services with their requirements</li>
              <li>Click "Search" to find suitable time slots</li>
            </ol>
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Defining Services
          </Typography>
          <Typography variant="body2" paragraph>
            A "service" represents any maritime activity with specific duration, tide, and time requirements:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary">Example: Boat Launch</Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ paddingLeft: '1rem' }}>
                      <li><b>Duration:</b> 2 hours</li>
                      <li><b>Launch requirement:</b> Minimum 1.8m tide height</li>
                      <li><b>Retrieval requirement:</b> Minimum 1.8m tide height</li>
                      <li><b>Time:</b> 8:00 AM - 4:00 PM (latest start)</li>
                    </ul>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    This ensures boats can be launched and retrieved with sufficient water depth.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary">Example: Beach Maintenance</Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ paddingLeft: '1rem' }}>
                      <li><b>Duration:</b> 4 hours</li>
                      <li><b>Launch requirement:</b> None (uncheck)</li>
                      <li><b>Retrieval requirement:</b> None (uncheck)</li>
                      <li><b>Time:</b> 6:00 AM - 12:00 PM (latest start)</li>
                    </ul>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    For activities that don't depend on tide height but have time restrictions.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary">Example: Bridge Inspection</Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ paddingLeft: '1rem' }}>
                      <li><b>Duration:</b> 1 hour</li>
                      <li><b>Launch requirement:</b> Minimum 2.5m tide height</li>
                      <li><b>Retrieval requirement:</b> None (uncheck)</li>
                      <li><b>Time:</b> 9:00 AM - 3:00 PM (latest start)</li>
                    </ul>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    When you need high tide to start but can finish regardless of tide height.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary">Example: Scuba Diving Tour</Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ paddingLeft: '1rem' }}>
                      <li><b>Duration:</b> 3 hours</li>
                      <li><b>Launch requirement:</b> Minimum 1.5m tide height</li>
                      <li><b>Retrieval requirement:</b> Minimum 1.5m tide height</li>
                      <li><b>Time:</b> 8:00 AM - 2:00 PM (latest start)</li>
                    </ul>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    For activities requiring specific tide conditions for both start and end times.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Typography variant="subtitle1" sx={{ mt: 3 }} gutterBottom fontWeight="bold">
            Interpreting Results
          </Typography>
          <Typography variant="body2" paragraph>
            After searching, the calendar view highlights days with viable service opportunities. Select a day to see:
          </Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li>A tide chart showing water heights throughout the day</li>
              <li>A list of service opportunities with start and end times</li>
              <li>Toggle "Show Optimized Schedule" to see a conflict-free schedule</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGuide} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;
